import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
  Container,
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useLeagueMembership } from "../context/LeagueMembershipContext";
import { useToast } from "../context/ToastContext";
import ModalDialog from "../Common/ModalDialog";
import { useRequest } from "../hooks/useRequest";
import { getTeamPlayersUrl, getTeamUrl } from "../config/api";
import { useLeague } from "../hooks/useLeague";
import { apiPost, apiPatch, apiDelete } from "../utils/apiClient";
import { getApiErrorMessages } from "../utils/apiErrors";
import { useParams } from "react-router-dom";
import PlayerPickerModal from "./PlayerPickerModal";
import { API_BASE_URL } from "../config/api";
import { PaginatedResponse } from "../types/api";
import TeamDisplay from "../Common/TeamDisplay";

// Type Definitions
interface TeamPlayer {
  id: number;
  team_player_id: number;
  player_id: number;
  team_id: number;
  first_name: string;
  last_name: string;
  jersey: string | null;
  position: string | null;
  position_id: number | null;
  discarded_at: string | null;
}

interface Team {
  id: number;
  name: string;
  season_id: number;
}

interface Position {
  id: number;
  name: string;
  abbr: string;
}

// Hardcoded positions - these are static reference data from the backend seeds
const POSITIONS: Position[] = [
  { id: 1, name: "Forward", abbr: "F" },
  { id: 2, name: "Defenceman", abbr: "D" },
  { id: 3, name: "Goaltender", abbr: "G" },
  { id: 4, name: "Center", abbr: "C" },
  { id: 5, name: "Right Wing", abbr: "RW" },
  { id: 6, name: "Left Wing", abbr: "LW" },
];

// Editable fields of a team player, in the shape the form inputs use.
interface TeamPlayerDraft {
  jersey: string;
  positionId: string;
}

// Pending edits keyed by team_player_id. Rows without an entry show their
// saved values, so a refetch after a partial save shows the new server state.
type DraftsById = Record<number, TeamPlayerDraft>;

type RowErrorsById = Record<number, string[]>;

type SortField = "last_name" | "first_name" | "jersey" | "position";
type SortDirection = "asc" | "desc";

// Helper Components
function TableLoadingView() {
  return (
    <Container>
      <Row className="justify-content-md-center">
        <Col md="auto">
          <Spinner animation="grow" /> Loading...
        </Col>
      </Row>
    </Container>
  );
}

// Sorting utility function
function sortTeamPlayers(
  players: TeamPlayer[],
  field: SortField,
  direction: SortDirection,
): TeamPlayer[] {
  const sorted = [...players].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;

    if (field === "jersey") {
      // Handle jersey numbers - convert to numbers for proper sorting
      aVal = parseInt(a[field] ?? "") || 0;
      bVal = parseInt(b[field] ?? "") || 0;
    } else {
      aVal = (a[field] ?? "").toLowerCase();
      bVal = (b[field] ?? "").toLowerCase();
    }

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
}

function savedDraft(teamPlayer: TeamPlayer): TeamPlayerDraft {
  return {
    jersey: teamPlayer.jersey ?? "",
    positionId: teamPlayer.position_id?.toString() ?? "",
  };
}

function isDraftChanged(teamPlayer: TeamPlayer, draft: TeamPlayerDraft) {
  const saved = savedDraft(teamPlayer);
  return (
    draft.jersey.trim() !== saved.jersey ||
    draft.positionId !== saved.positionId
  );
}

// TeamPlayersList Component
function TeamPlayersList({
  onAddPlayerClick,
  onRemoveClick,
  teamId,
  team,
  refreshKey,
}: {
  onAddPlayerClick: () => void;
  onRemoveClick: (teamPlayer: TeamPlayer) => void;
  teamId: number;
  team: Team;
  refreshKey: number;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const { canManageLeague, canScoreGames } = useLeagueMembership();
  const { showToast } = useToast();
  const [sortField, setSortField] = useState<SortField>("last_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [drafts, setDrafts] = useState<DraftsById>({});
  const [rowErrors, setRowErrors] = useState<RowErrorsById>({});
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  // Bumped after saving so the list refetches without remounting and losing
  // the edit state of rows that failed to save.
  const [reloadKey, setReloadKey] = useState(0);

  const { data: response, error } = useRequest<PaginatedResponse<TeamPlayer>>(
    `${getTeamPlayersUrl(teamId)}?_refresh=${refreshKey}-${reloadKey}`,
    auth.token,
  );

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort indicator component
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="text-muted"> ⇅</span>;
    return <span>{sortDirection === "asc" ? " ▲" : " ▼"}</span>;
  };

  // Get sorted players
  const sortedPlayers = response?.data
    ? sortTeamPlayers(response.data, sortField, sortDirection)
    : [];

  const draftFor = (teamPlayer: TeamPlayer): TeamPlayerDraft =>
    drafts[teamPlayer.team_player_id] ?? savedDraft(teamPlayer);

  const updateDraft = (
    teamPlayer: TeamPlayer,
    changes: Partial<TeamPlayerDraft>,
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [teamPlayer.team_player_id]: {
        ...(prev[teamPlayer.team_player_id] ?? savedDraft(teamPlayer)),
        ...changes,
      },
    }));
  };

  const resetEditState = () => {
    setDrafts({});
    setRowErrors({});
    setSaveErrorMessage("");
  };

  const handleEditClick = () => {
    resetEditState();
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    resetEditState();
    setIsEditing(false);
  };

  const handleSaveClick = async () => {
    const changedPlayers = sortedPlayers.filter((teamPlayer) => {
      const draft = drafts[teamPlayer.team_player_id];
      return draft !== undefined && isDraftChanged(teamPlayer, draft);
    });

    if (changedPlayers.length === 0) {
      handleCancelClick();
      return;
    }

    setIsSaving(true);
    setRowErrors({});
    setSaveErrorMessage("");

    const results = await Promise.allSettled(
      changedPlayers.map((teamPlayer) => {
        const draft = drafts[teamPlayer.team_player_id];
        return apiPatch(
          `${API_BASE_URL}/team_players/${teamPlayer.team_player_id}`,
          {
            team_player: {
              jersey: draft.jersey.trim() || null,
              position_id: parseInt(draft.positionId) || null,
            },
          },
          {
            token: auth.token,
            ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
          },
        );
      }),
    );

    const failedDrafts: DraftsById = {};
    const failedRowErrors: RowErrorsById = {};
    results.forEach((result, index) => {
      if (result.status === "fulfilled") return;
      const teamPlayer = changedPlayers[index];
      console.error("Error updating team player:", result.reason);
      failedDrafts[teamPlayer.team_player_id] =
        drafts[teamPlayer.team_player_id];
      failedRowErrors[teamPlayer.team_player_id] = getApiErrorMessages(
        result.reason,
        "An error occurred while updating this player.",
      );
    });

    const failedCount = Object.keys(failedDrafts).length;
    const savedCount = changedPlayers.length - failedCount;

    setIsSaving(false);
    if (savedCount > 0) setReloadKey((key) => key + 1);

    if (failedCount === 0) {
      resetEditState();
      setIsEditing(false);
      showToast(
        `Updated ${savedCount} player${savedCount === 1 ? "" : "s"}.`,
        "success",
      );
      return;
    }

    // Keep only the failed rows' edits so they can be corrected and retried.
    setDrafts(failedDrafts);
    setRowErrors(failedRowErrors);
    setSaveErrorMessage(
      savedCount > 0
        ? `Saved ${savedCount} of ${changedPlayers.length} changed players. The players below could not be saved.`
        : "The players below could not be saved.",
    );
  };

  const hasPlayers = !!response && response.data.length > 0;

  return (
    <div className="admin-content">
      <Container>
        <Row>
          <Col md={8}>
            <h4>
              <TeamDisplay team={team} /> - Players
            </h4>
            {/* Editing jerseys and positions is open to scorekeepers, who
                reconcile numbers against the sheet at the rink; adding and
                removing players stays with league staff. */}
            {canScoreGames && (
              <Row className="mb-2">
                <Col className="d-flex justify-content-end gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        onClick={handleCancelClick}
                        variant="secondary"
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveClick}
                        variant="primary"
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />{" "}
                            Saving...
                          </>
                        ) : (
                          "Save"
                        )}
                      </Button>
                    </>
                  ) : (
                    <>
                      {hasPlayers && (
                        <Button onClick={handleEditClick} variant="primary">
                          Edit
                        </Button>
                      )}
                      {canManageLeague && (
                        <Button
                          onClick={() => onAddPlayerClick()}
                          variant="primary"
                        >
                          Add Player
                        </Button>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                {error && (
                  <div className="alert alert-danger" role="alert">
                    Error loading players: {error.message}
                  </div>
                )}
                {isEditing && saveErrorMessage && (
                  <Alert variant="danger">{saveErrorMessage}</Alert>
                )}
                {!response && <TableLoadingView />}
                {response &&
                  response.data.length === 0 &&
                  (canManageLeague ? (
                    <p>
                      Click `Add Player` to add your first player to this team.
                    </p>
                  ) : (
                    <p>This team has no players yet.</p>
                  ))}
                {hasPlayers && (
                  <Table striped bordered hover className="crud">
                    <thead>
                      <tr>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("last_name")}
                        >
                          Last Name
                          <SortIndicator field="last_name" />
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("first_name")}
                        >
                          First Name
                          <SortIndicator field="first_name" />
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("jersey")}
                        >
                          Jersey
                          <SortIndicator field="jersey" />
                        </th>
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSort("position")}
                        >
                          Position
                          <SortIndicator field="position" />
                        </th>
                        {canManageLeague && !isEditing && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPlayers.map((teamPlayer: TeamPlayer) => {
                        const playerName = `${teamPlayer.first_name} ${teamPlayer.last_name}`;
                        const draft = draftFor(teamPlayer);
                        const errors = rowErrors[teamPlayer.team_player_id];

                        return (
                          <tr key={teamPlayer.team_player_id}>
                            <td>{teamPlayer.last_name}</td>
                            <td>{teamPlayer.first_name}</td>
                            {isEditing ? (
                              <>
                                <td>
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Jersey"
                                    aria-label={`Jersey number for ${playerName}`}
                                    value={draft.jersey}
                                    isInvalid={!!errors}
                                    disabled={isSaving}
                                    onChange={(event) =>
                                      updateDraft(teamPlayer, {
                                        jersey: event.target.value,
                                      })
                                    }
                                  />
                                  {errors && (
                                    <Form.Control.Feedback type="invalid">
                                      {errors.join(" ")}
                                    </Form.Control.Feedback>
                                  )}
                                </td>
                                <td>
                                  <Form.Select
                                    size="sm"
                                    aria-label={`Position for ${playerName}`}
                                    value={draft.positionId}
                                    isInvalid={!!errors}
                                    disabled={isSaving}
                                    onChange={(event) =>
                                      updateDraft(teamPlayer, {
                                        positionId: event.target.value,
                                      })
                                    }
                                  >
                                    <option value="">No Position</option>
                                    {POSITIONS.map((position) => (
                                      <option
                                        key={position.id}
                                        value={position.id}
                                      >
                                        {position.name} ({position.abbr})
                                      </option>
                                    ))}
                                  </Form.Select>
                                </td>
                              </>
                            ) : (
                              <>
                                <td>{teamPlayer.jersey}</td>
                                <td>{teamPlayer.position}</td>
                              </>
                            )}
                            {canManageLeague && !isEditing && (
                              <td align="right">
                                <Button
                                  variant="danger"
                                  onClick={() => onRemoveClick(teamPlayer)}
                                >
                                  Remove
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

// Main Component
function AdminTeamPlayersPage() {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const { teamId } = useParams();
  const [content, setContent] = useState<React.ReactNode>(null);
  const [showPlayerPicker, setShowPlayerPicker] = useState(false);
  const [refreshKey, setRefreshKey] = useState(Date.now());

  const teamIdNum = teamId ? parseInt(teamId, 10) : 0;

  // Fetch team details
  const { data: team } = useRequest<Team>(
    teamIdNum > 0 ? getTeamUrl(teamIdNum) : undefined,
    auth.token,
  );

  // Fetch team players to get existing player IDs
  // Add refreshKey to URL to bust cache when data changes
  const { data: teamPlayersResponse } = useRequest<
    PaginatedResponse<TeamPlayer>
  >(
    teamIdNum > 0
      ? `${getTeamPlayersUrl(teamIdNum)}?_refresh=${refreshKey}`
      : undefined,
    auth.token,
  );

  // Extract existing player IDs
  const existingPlayerIds =
    teamPlayersResponse?.data.map((tp) => tp.player_id) || [];

  // Navigation functions
  function showList() {
    if (!team) return;
    setContent(
      <TeamPlayersList
        key={refreshKey}
        onAddPlayerClick={() => setShowPlayerPicker(true)}
        onRemoveClick={confirmRemove}
        teamId={teamIdNum}
        team={team}
        refreshKey={refreshKey}
      />,
    );
  }

  function confirmRemove(teamPlayer: TeamPlayer) {
    setContent(
      <div
        className="modal show"
        style={{ display: "block", position: "initial" }}
      >
        <ModalDialog
          titleText="Remove Player Confirmation"
          bodyText={`Are you sure you want to remove ${teamPlayer.first_name} ${teamPlayer.last_name} from this team?`}
          okText="Remove"
          onCancelClick={() => {
            console.debug("Remove canceled");
            showList();
          }}
          onSaveClick={() => {
            console.debug("Removing player:", teamPlayer);

            apiDelete(
              `${API_BASE_URL}/team_players/${teamPlayer.team_player_id}`,
              {
                token: auth.token,
                ...(currentLeague?.abbr
                  ? { leagueAbbr: currentLeague.abbr }
                  : {}),
              },
            )
              .then(() => {
                console.debug("Player removed successfully");
                setRefreshKey(Date.now());
                showList();
              })
              .catch((error) => {
                console.error("Error removing player:", error);
                showList();
              });
          }}
        />
      </div>,
    );
  }

  // Handle player selection from picker modal
  const handlePlayerSelected = (
    playerId: number,
    positionId: number,
    jersey: string,
  ) => {
    console.debug("Adding player to team:", playerId, positionId, jersey);

    const requestBody = {
      team_player: {
        player_id: playerId,
        position_id: positionId || null,
        jersey: jersey || null,
      },
    };

    apiPost(getTeamPlayersUrl(teamIdNum), requestBody, {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then((result) => {
        console.debug("Player added successfully:", result);
        // Don't close modal - allow adding multiple players
        // Update refresh key to show newly added player with "On Team" badge
        setRefreshKey(Date.now());
        showList();
      })
      .catch((error) => {
        console.error("Error adding player:", error);
        alert(error.message || "An error occurred while adding the player");
      });
  };

  // Initialize with list view when team or seasonId changes
  useEffect(() => {
    if (teamIdNum > 0 && team) {
      showList();
    }
    // showList is intentionally not in deps to avoid recreating on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLeague, teamId, team]);

  return (
    <div className="mt-4">
      <h3 className="mb-4">{team?.name || "Team"} Players</h3>
      {content}

      {showPlayerPicker && (
        <PlayerPickerModal
          show={showPlayerPicker}
          onClose={() => setShowPlayerPicker(false)}
          onPlayerSelected={handlePlayerSelected}
          teamId={teamIdNum}
          existingPlayerIds={existingPlayerIds}
        />
      )}
    </div>
  );
}

export default AdminTeamPlayersPage;
